import message from "@/utils/globalMessage";
import React, { useMemo, useRef, useState } from "react";
import { Popconfirm } from "antd";
import ResponsiveProTable from "@components/ResponsiveProTable";
import moment from "moment";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useAuth } from "@/hooks/use-auth";
import Select from "@components/ResponsiveSelect";

const AdminBenchmark = () => {
  const actionRef = useRef();
  const [selectedCompany, setSelectedCompany] = useState();

  const canDelete = useAuth('admin_benchmark_menu', 3);
  const companyOptions = useSelector(state => state.Company?.data || []);
  const companySelectOptions = useMemo(
    () =>
      (companyOptions || []).map(company => ({
        label: company.label,
        value: company.id,
      })),
    [companyOptions]
  );

  // 数字格式化：整数不带小数，小数最多两位，带千分位
  const formatNumber = (value) => {
    if (value == null || value === "" || value === "-") {
      return value || "-";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
      return value;
    }
    if (Number.isInteger(num)) {
      return num.toLocaleString("en-US");
    }
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteBenchmark(id);
      message.success("Deleted successfully");
      if (actionRef.current) {
        actionRef.current.reload();
      }
    } catch (error) {
      message.error("Delete failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        width: 180,
        ellipsis: true,
      },
      {
        title: "Facility",
        dataIndex: "facility",
        key: "facility",
        width: 200,
        ellipsis: true,
      },
      {
        title: "Year",
        dataIndex: "year",
        key: "year",
        width: 80,
        render: (text, record) => moment(record.year).format("YYYY"),
      },
      {
        title: "Size (sqft)",
        dataIndex: "size",
        key: "size",
        width: 120,
        render: (text) => formatNumber(text),
      },
      {
        title: "Category",
        dataIndex: "facility_category_name",
        key: "facility_category_name",
        width: 160,
        ellipsis: true,
      },
      {
        title: "EUI (kBtu/q-yr)",
        dataIndex: "eui",
        key: "eui",
        width: 140,
      },
      {
        title: "EUI - Elec(kBtu/sq-yr)",
        dataIndex: "eui_elec",
        key: "eui_elec",
        width: 180,
      },
      {
        title: "EUI - NG (kBtu/sq-yr)",
        dataIndex: "eui_ng",
        key: "eui_ng",
        width: 180,
      },
      {
        title: "Energy Costs ($)",
        dataIndex: "energy_costs",
        key: "energy_costs",
        width: 150,
        render: (text) => formatNumber(text),
      },
      {
        title: "Energy Costs ($/sqft)",
        dataIndex: "energy_costs_sqft",
        key: "energy_costs_sqft",
        width: 180,
        render: (text) => formatNumber(text),
      },
      {
        title: "Region",
        dataIndex: "region",
        key: "region",
        width: 120,
        ellipsis: true,
      },
      {
        title: "City",
        dataIndex: "city",
        key: "city",
        width: 120,
        ellipsis: true,
      },
      {
        title: "State",
        dataIndex: "state",
        key: "state",
        width: 100,
        ellipsis: true,
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        width: 120,
        ellipsis: true,
      },
      {
        title: "Climate Zone",
        dataIndex: "climate_zone",
        key: "climate_zone",
        width: 120,
      },
      {
        title: "Modified Date",
        dataIndex: "modified_date",
        key: "modified_date",
        width: 180,
        render: (text) => {
          if (text && moment(text).isValid()) {
            return moment(text).format("YYYY-MM-DD HH:mm");
          }
          return "-";
        },
      },
      ...(canDelete
        ? [
            {
              title: "Action",
              key: "action",
              width: 100,
              fixed: "right",
              render: (_, record) => (
                <Popconfirm
                  title="Confirm delete?"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <a>Delete</a>
                </Popconfirm>
              ),
            },
          ]
        : []),
    ],
    [canDelete]
  );

  const handleCompanyFilterChange = (value) => {
    setSelectedCompany(value);
    if (actionRef.current) {
      actionRef.current.reload();
    }
  };

  const requestTable = async (params) => {
    const { current = 1, pageSize = 20 } = params;
    const query = new URLSearchParams();
    query.append("page", current);
    query.append("page_size", pageSize);
    if (selectedCompany) {
      query.append("company", selectedCompany);
    }
    try {
      const res = await api.getAdminFacilityBenchmark(query.toString());
      return {
        data: res.data?.results || [],
        success: true,
        total: res.data?.count || 0,
      };
    } catch (error) {
      message.error("Failed to load data");
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };

  return (
    <div style={{padding: 12}}>
      <div style={{ marginBottom: 12 }}>
        <Select
          showSearch
          allowClear
          placeholder="Filter by Company"
          style={{ width: 220 }}
          value={selectedCompany}
          onChange={handleCompanyFilterChange}
          options={companySelectOptions}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>
      <ResponsiveProTable
        rowKey="id"
        actionRef={actionRef}
        columns={columns}
        request={requestTable}
        search={false}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
        }}
        scroll={{ x: 1500 }}
        options={{
          reload: true,
          setting: true,
          density: false,
          fullScreen: false,
        }}
      />
    </div>
  );
};

export default AdminBenchmark;
